import useAuthStore from '../stores/useAuthStore';
import ordens from '../data/ordens.json';
import produtos from '../data/produtos.json';
import maquinas from '../data/maquinas.json';
import roteiros from '../data/roteiros.json';
import apontamentos from '../data/apontamentos.json';
import usuarios from '../data/usuarios.json';
import alertas from '../data/alertas.json';
import manutencoes from '../data/manutencoes.json';
import agendamentos from '../data/agendamentos.json';
import centrosTrabalho from '../data/centrosTrabalho.json';
import equipe from '../data/equipe.json';
import materiais from '../data/materiais.json';
import clientes from '../data/clientes.json';
import planejamento from '../data/planejamento.json';
import setups from '../data/setups.json';
import configData from '../data/config.json';
import zh6 from '../data/zh6.json';

let nextAlertaId = alertas.length + 1;
let nextManutencaoId = manutencoes.length + 1;
let nextAgendamentoId = agendamentos.length + 1;
let nextUsuarioId = usuarios.length + 1;
let nextPlanejamentoId = planejamento.length + 1;
let nextSetupId = setups.length + 1;
const manutencaoDb = [...manutencoes];
const agendamentoDb = [...agendamentos];
const alertaDb = [...alertas];
const usuarioDb = [...usuarios];
const planejamentoDb = [...planejamento];
const setupDb = [...setups];

function findProduto(codigo) {
  return produtos.find(p => p.B1_COD === codigo);
}

function findMaquina(codigo) {
  return maquinas.find(m => m.H1_CODIGO === codigo);
}

// Monta a linha CRUA de SC2 (mesmo formato que o backend real devolve no
// GET /api/protheus/ordens) a partir de um registro de ordens.json — a tela
// OrdensProducao.jsx monta o código completo como C2_NUM + C2_ITEM +
// C2_SEQUEN e espera esses campos presentes (mock antigo devolvia a linha
// já normalizada, gerando "undefinedundefinedundefined" no lugar do código).
function linhaProtheusOrdem(row) {
  const codigoCompleto = `${row.C2_NUM}${row.C2_ITEM}${row.C2_SEQUEN}`;
  const apontamentosDaOp = apontamentos.filter(a => a.H6_OP === codigoCompleto);
  const produzidoPorOperacao = {};
  const refugadoPorOperacao = {};
  apontamentosDaOp.forEach(a => {
    const op = a.H6_OPERAC;
    produzidoPorOperacao[op] = (produzidoPorOperacao[op] || 0) + (a.H6_QTDPROD || 0);
    refugadoPorOperacao[op] = (refugadoPorOperacao[op] || 0) + (a.H6_QTDPERD || 0);
  });
  return {
    ...row,
    _PRODUTO_NOME: findProduto(row.C2_PRODUTO)?.B1_DESC || '',
    _REFUGADO: apontamentosDaOp.reduce((s, a) => s + (a.H6_QTDPERD || 0), 0),
    _STATUS_REAL: null,
    _STATUS_POR_OPERACAO: {},
    _SESSAO_POR_OPERACAO: {},
    _META_AJUSTADA_PRIMEIRA_OPERACAO: null,
    _META_POR_OPERACAO: {},
    _PRODUZIDO_POR_OPERACAO: produzidoPorOperacao,
    _REFUGADO_POR_OPERACAO: refugadoPorOperacao,
  };
}

function normalizarOrdem(row) {
  const produto = findProduto(row.C2_PRODUTO);
  return {
    id: `${row.C2_NUM}${row.C2_ITEM}${row.C2_SEQUEN}`,
    codigo: row.C2_NUM,
    item: row.C2_ITEM,
    sequencia: row.C2_SEQUEN,
    produto: row.C2_PRODUTO,
    produto_nome: produto?.B1_DESC || 'Produto Desconhecido',
    quantidade: row.C2_QUANT,
    produzido_total: row.C2_QUJE || 0,
    data_prevista: row.C2_DATPRF,
    data_emissao: row.C2_EMISSAO,
    roteiro: row.C2_ROTEIRO,
    local: row.C2_LOCAL,
    status: row.C2_STATUS === 'N' ? 'NAO INICIADA' : row.C2_STATUS === 'P' ? 'EM PRODUCAO' : 'FINALIZADA',
    recurso: row.C2_RECURSO || '',
    imagem_url: '',
    desenho_url: '',
    pfi_url: ''
  };
}

function buildResumo() {
  const ods = ordens.map(normalizarOrdem);
  const totalProduzido = apontamentos.reduce((s, a) => s + (a.H6_QTDPROD || 0), 0);
  const totalAProduzir = ods.reduce((s, o) => s + o.quantidade, 0);
  const maquinasAtivas = maquinas.filter(m => m.ativo).length;
  const now = new Date();
  const producaoPorHora = [];
  for (let i = 7; i <= 17; i++) {
    producaoPorHora.push({
      hora: `${String(i).padStart(2, '0')}:00`,
      produzido: Math.floor(Math.random() * 50) + 10,
      refugado: Math.floor(Math.random() * 5)
    });
  }
  return {
    producaoAtual: totalProduzido,
    quantidadeAProduzir: totalAProduzir - totalProduzido,
    maquinasAtivas,
    totalMaquinas: maquinas.length,
    producaoPorHora,
    ordensAtivas: ods.slice(0, 5)
  };
}

function buildGantt(inicio, fim) {
  const maqMap = {};
  // Data AAAAMMDD + hora "HH:MM" -> "AAAA-MM-DDTHH:MM" (ISO válido pro
  // construtor Date). O Gantt faz new Date(bloco.inicio/fim) — sem isso,
  // "20260920T07:00" vira Invalid Date e os blocos param de posicionar.
  const paraISO = (dataAAAAMMDD, hora) => (dataAAAAMMDD && hora)
    ? `${String(dataAAAAMMDD).slice(0, 4)}-${String(dataAAAAMMDD).slice(4, 6)}-${String(dataAAAAMMDD).slice(6, 8)}T${hora}`
    : '';
  maquinas.filter(m => m.ativo && m.H1_CTRAB !== '008').forEach(m => {
    if (!maqMap[m._CENTRO_TRABALHO]) maqMap[m._CENTRO_TRABALHO] = [];
    maqMap[m._CENTRO_TRABALHO].push({
      codigo: m.H1_CODIGO,
      nome: m.H1_DESCRI,
      blocos: [],
      planejado: [],
      fila: []
    });
  });

  // Códigos reais da ZH6_STATUS pro vocabulário do Gantt (18/09/2026) — o
  // mock antigo mapeava E->EXECUCAO/FINALIZADO, que não existem mais em
  // STATUS_COLORS (ver ChaoFabrica.jsx) e fazia todo bloco renderizar cinza
  // "Encerrado". Mesmo vocabulário real que o backend devolve hoje.
  const ZH6_STATUS_REAL = {
    E: 'EM PRODUCAO',
    P: 'PARADO',
    F: 'CONCLUIDA',
    A: 'AGUARDANDO',
    S: 'SETUP_ANDAMENTO',
    R: 'REPREPARACAO_ANDAMENTO',
    M: 'MANUTENCAO_ANDAMENTO',
    I: 'IMPRODUTIVO_ANDAMENTO',
  };

  zh6.forEach(z => {
    const maq = maquinas.find(m => m.H1_CODIGO === z.ZH6_RECURSO);
    if (!maq) return;
    const grupo = maq._CENTRO_TRABALHO;
    const maqEntry = maqMap[grupo]?.find(mc => mc.codigo === z.ZH6_RECURSO);
    if (!maqEntry) return;
    const codProduto = ordens.find(o => `${o.C2_NUM}${o.C2_ITEM}${o.C2_SEQUEN}` === z.ZH6_OP)?.C2_PRODUTO;
    const prod = findProduto(codProduto);
    // ZH6 do mock não traz a operação; sai do roteiro do produto naquela
    // máquina (o bloco mostrava "· Op." vazio).
    const operacao = roteiros.find(r => r.G2_PRODUTO === codProduto && r.G2_RECURSO === z.ZH6_RECURSO)?.G2_OPERAC
      || roteiros.find(r => r.G2_PRODUTO === codProduto)?.G2_OPERAC
      || '010';
    maqEntry.blocos.push({
      op: z.ZH6_OP,
      operacao,
      produto: prod?.B1_DESC || 'Produto',
      inicio: paraISO(z.ZH6_DATA, z.ZH6_HORA),
      fim: paraISO(z.ZH6_DATAFIN, z.ZH6_HORAFIN),
      status: ZH6_STATUS_REAL[z.ZH6_STATUS] || 'AGUARDANDO',
      produzido: Number(z.ZH6_QTDPROD) || 0
    });
  });

  agendamentoDb.forEach(a => {
    const maqEntry = Object.values(maqMap).flat().find(mc => mc.codigo === a.maquina);
    if (!maqEntry) return;
    maqEntry.planejado.push({
      id: a.id,
      tipo: a.tipo,
      op: a.op,
      produto: a.produtoNome,
      inicio: paraISO(a.dtini, a.hrini),
      fim: paraISO(a.dtfim, a.hrfim),
      observacao: a.observacao
    });
  });

  return {
    grupos: Object.entries(maqMap).map(([centro, maquinas]) => ({
      centroTrabalho: centro,
      maquinas
    }))
  };
}

async function fakeDelay(ms = 150) {
  return new Promise(r => setTimeout(r, ms));
}

function matchPath(path, pattern) {
  const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '([^/]+)') + '$');
  const match = path.split('?')[0].match(regex);
  return match ? match.slice(1) : null;
}

function getQueryParams(path) {
  const idx = path.indexOf('?');
  if (idx === -1) return {};
  const params = {};
  const qs = path.slice(idx + 1).split('&');
  for (const p of qs) {
    const [k, v] = p.split('=');
    params[decodeURIComponent(k)] = decodeURIComponent(v || '');
  }
  return params;
}

function handleGet(path) {
  const base = path.split('?')[0];
  const q = getQueryParams(path);

  if (base === '/api/auth/login') return { token: 'fake-jwt-token', user: usuarioDb[0] };

  if (base === '/api/producao/resumo') return buildResumo();

  if (base === '/api/producao/chao-fabrica') {
    return buildGantt(q.inicio || '', q.fim || '');
  }

  if (base === '/api/producao/maquinas-disponiveis') {
    return {
      maquinasDisponiveis: maquinas.filter(m => m.ativo && m.H1_CTRAB !== '008').map(m => ({
        codigo: m.H1_CODIGO,
        nome: m.H1_DESCRI
      })),
      totalMaquinasCentroTrabalho: maquinas.filter(m => m.ativo).length
    };
  }

  const sessaoMatch = matchPath(base, '/api/producao/:id/sessao');
  if (sessaoMatch) {
    const opId = sessaoMatch[0];
    const ordem = ordens.find(o => `${o.C2_NUM}${o.C2_ITEM}${o.C2_SEQUEN}` === opId || o.C2_NUM === opId);
    if (!ordem) return { ordem: {}, apontamentos: [] };
    return {
      ordem: normalizarOrdem(ordem),
      apontamentos: apontamentos.filter(a => a.H6_OP === opId)
    };
  }

  const materiaisMatch = matchPath(base, '/api/producao/:id/materiais');
  if (materiaisMatch) {
    const jaRequisitado = Math.random() > 0.5;
    return {
      materiais: materiais.map(m => ({
        codigo: m.D4_COD,
        armazem: m.D4_LOCAL,
        quantidade_disponivel: m.D4_QUANT,
        quantidade_pendente: 0,
        quantidade_solicitada: jaRequisitado ? m.D4_QUANT : 0
      })),
      ja_requisitado: jaRequisitado
    };
  }

  if (base === '/api/producao/em-andamento') {
    return null;
  }

  if (base === '/api/producao/documentos') {
    return { documentos: [] };
  }

  if (base === '/api/protheus/ordens') {
    const limit = parseInt(q.limit) || 20;
    const pagina = parseInt(q.pagina) || 1;
    let filtered = [...ordens];
    if (q.busca) {
      const b = q.busca.toLowerCase();
      filtered = filtered.filter(o => o.C2_NUM.includes(b) || o.C2_PRODUTO.includes(b));
    }
    if (q.dataInicio) {
      filtered = filtered.filter(o => !o.C2_DATPRF || o.C2_DATPRF >= q.dataInicio);
    }
    if (q.dataFim) {
      filtered = filtered.filter(o => !o.C2_DATPRF || o.C2_DATPRF <= q.dataFim);
    }
    const start = (pagina - 1) * limit;
    const paginated = filtered.slice(start, start + limit);
    return {
      ordens: paginated.map(linhaProtheusOrdem),
      temMais: start + limit < filtered.length
    };
  }

  const ordemUnicaMatch = matchPath(base, '/api/protheus/ordens/:num');
  if (ordemUnicaMatch) {
    const found = ordens.find(o => o.C2_NUM === ordemUnicaMatch[0]);
    return found ? normalizarOrdem(found) : null;
  }

  if (base === '/api/protheus/maquinas') {
    return maquinas.map(m => ({
      codigo: m.H1_CODIGO,
      nome: m.H1_DESCRI,
      centroTrabalho: m._CENTRO_TRABALHO,
      capacidadeHorut: m._CAPACIDADE_HRUT,
      ativo: m.ativo
    }));
  }

  if (base === '/api/protheus/roteiro') {
    const produto = q.produto;
    const roteiro = q.roteiro;
    return roteiros
      .filter(r => r.G2_PRODUTO === produto && (!roteiro || r.G2_CODIGO === roteiro))
      .sort((a, b) => a.G2_OPERAC.localeCompare(b.G2_OPERAC));
  }

  if (base === '/api/protheus/apontamentos') {
    return apontamentos;
  }

  if (base === '/api/protheus/centros-trabalho') {
    return centrosTrabalho;
  }

  if (base === '/api/maquinas') {
    return maquinas.map(m => ({
      id: m.H1_CODIGO,
      codigo_protheus: m.H1_CODIGO,
      nome: m.H1_DESCRI,
      status_operacional: m.status_operacional,
      ativo: m.ativo,
      horas_mensais: m.horas_mensais,
      centro_trabalho: m._CENTRO_TRABALHO,
      capacidade_horut: m._CAPACIDADE_HRUT
    }));
  }

  if (base === '/api/alertas') return [...alertaDb];

  if (base === '/api/manutencao') return [...manutencaoDb];

  if (base === '/api/equipe') return [...equipe];

  if (base === '/api/usuarios') return usuarioDb.map(u => ({ ...u, senha: undefined }));

  if (base === '/api/usuarios/funcionarios-disponiveis') {
    const comLogin = new Set(usuarioDb.filter(u => u.matricula).map(u => u.matricula));
    return [
      { matricula: '000103', nome: 'Pedro Oliveira', cargo: 'Operador de Coladeira' },
      { matricula: '000105', nome: 'Carlos Pereira', cargo: 'Operador de Empilhadeira' },
      { matricula: '000106', nome: 'Lucia Ferreira', cargo: 'Auxiliar de Expedicao' }
    ].filter(f => !comLogin.has(f.matricula));
  }

  if (base === '/api/config/config') return { ...configData };

  if (base === '/api/agendamento') return agendamentoDb.map(a => ({ ...a }));

  if (base === '/api/setup') return setupDb.map(s => ({ ...s }));

  if (base === '/api/planejamento') {
    const data = q.data;
    const itens = planejamentoDb.filter(p => p.data === data);
    return {
      itens,
      status: itens.length === 0 ? 'NAO_CRIADO' : itens[0].status
    };
  }

  if (base === '/api/clientes') return [...clientes];

  return null;
}

function handlePost(path, body) {
  const base = path.split('?')[0];

  if (base === '/api/producao/sincronizar-protheus') {
    const vistos = new Set();
    return ordens.map(normalizarOrdem).filter((o) => {
      if (vistos.has(o.codigo)) return false;
      vistos.add(o.codigo);
      return true;
    });
  }

  const iniciarMatch = matchPath(base, '/api/producao/:id/iniciar');
  if (iniciarMatch) {
    return { hora_inicio_turno: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) };
  }

  const apontarMatch = matchPath(base, '/api/producao/:id/apontar');
  if (apontarMatch) {
    return {
      apontamento: {
        id: Date.now(),
        produzido: body?.produzido || 0,
        refugado: body?.refugado || 0,
        operador: body?.operador || 'Admin',
        operacao: body?.operacao || '010',
        recurso: body?.recurso || '000101',
        data: new Date().toISOString().slice(0, 10).replace(/-/g, '')
      }
    };
  }

  const encerrarMatch = matchPath(base, '/api/producao/:id/encerrar');
  if (encerrarMatch) return { sucesso: true };

  const requisitarMatch = matchPath(base, '/api/producao/:id/requisitar-materiais');
  if (requisitarMatch) return { sucesso: true };

  if (base === '/api/reatribuicoes') return { sucesso: true };

  if (base === '/api/reatribuicoes/cancelar') return { sucesso: true };

  if (base === '/api/alertas') {
    const novo = { id: nextAlertaId++, ...body, created_at: new Date().toISOString(), resolvido: false };
    alertaDb.push(novo);
    return novo;
  }

  if (base === '/api/manutencao') {
    const nova = { id: nextManutencaoId++, ...body };
    manutencaoDb.push(nova);
    return nova;
  }

  if (base === '/api/usuarios') {
    const novo = { id: nextUsuarioId++, ...body };
    usuarioDb.push(novo);
    return { ...novo, senha: undefined };
  }

  if (base === '/api/agendamento') {
    const novo = { id: nextAgendamentoId++, ...body };
    agendamentoDb.push(novo);
    return novo;
  }

  if (base === '/api/planejamento') {
    return { itens: body?.itens || [], status: 'PLANEJADO' };
  }

  if (base === '/api/planejamento/publicar') {
    return { itens: planejamentoDb.filter(p => p.data === body?.data), status: 'PUBLICADO' };
  }

  if (base === '/api/setup') {
    const novo = { id: nextSetupId++, ...body };
    setupDb.push(novo);
    return novo;
  }

  if (base === '/api/auth/login') {
    // Demonstração sem tela de login: credencial que não bate cai no primeiro usuário ativo.
    const u = usuarioDb.find(u => u.usuario === body?.usuario && u.senha === body?.senha)
      ?? usuarioDb.find(u => u.ativo);
    if (u) return { token: 'fake-jwt-token', user: { ...u, senha: undefined } };
    return null;
  }

  return { sucesso: true };
}

function handlePut(path, body) {
  const base = path.split('?')[0];

  const alertaStatusMatch = matchPath(base, '/api/alertas/:id/status');
  if (alertaStatusMatch) {
    const id = parseInt(alertaStatusMatch[0]);
    const a = alertaDb.find(a => a.id === id);
    if (a) a.status = body?.status || 'Reconhecido';
    return a;
  }

  const alertaResolverMatch = matchPath(base, '/api/alertas/:id/resolver');
  if (alertaResolverMatch) {
    const id = parseInt(alertaResolverMatch[0]);
    const a = alertaDb.find(a => a.id === id);
    if (a) { a.resolvido = true; a.resolvido_por = body?.resolvido_por; }
    return a;
  }

  const manutencaoMatch = matchPath(base, '/api/manutencao/:id');
  if (manutencaoMatch) {
    const id = parseInt(manutencaoMatch[0]);
    const idx = manutencaoDb.findIndex(m => m.id === id);
    if (idx >= 0) manutencaoDb[idx] = { ...manutencaoDb[idx], ...body };
    return manutencaoDb[idx];
  }

  const usuarioMatch = matchPath(base, '/api/usuarios/:id');
  if (usuarioMatch) {
    const id = parseInt(usuarioMatch[0]);
    const idx = usuarioDb.findIndex(u => u.id === id);
    if (idx >= 0) usuarioDb[idx] = { ...usuarioDb[idx], ...body };
    return { ...usuarioDb[idx], senha: undefined };
  }

  const maquinaMatch = matchPath(base, '/api/maquinas/:id');
  if (maquinaMatch) {
    const id = maquinaMatch[0];
    const m = maquinas.find(m => m.H1_CODIGO === id);
    if (m) {
      if (body?.ativo !== undefined) m.ativo = body.ativo;
      if (body?.horas_mensais !== undefined) m.horas_mensais = body.horas_mensais;
    }
    return {
      id: m?.H1_CODIGO,
      codigo_protheus: m?.H1_CODIGO,
      nome: m?.H1_DESCRI,
      status_operacional: m?.status_operacional,
      ativo: m?.ativo,
      horas_mensais: m?.horas_mensais
    };
  }

  const alocacaoMatch = matchPath(base, '/api/alocacoes/:mat');
  if (alocacaoMatch) {
    const mat = alocacaoMatch[0];
    const eq = equipe.find(e => e.matricula === mat);
    if (eq) {
      eq.maquina_alocada_codigo = body?.maquina || '';
      eq.maquina_alocada_nome = body?.maquina ? (maquinas.find(m => m.H1_CODIGO === body.maquina)?.H1_DESCRI || '') : '';
    }
    return { sucesso: true };
  }

  const agendamentoMatch = matchPath(base, '/api/agendamento/:id');
  if (agendamentoMatch) {
    const id = parseInt(agendamentoMatch[0]);
    const idx = agendamentoDb.findIndex(a => a.id === id);
    if (idx >= 0) agendamentoDb[idx] = { ...agendamentoDb[idx], ...body };
    return agendamentoDb[idx];
  }

  const setupMatch = matchPath(base, '/api/setup/:id');
  if (setupMatch) {
    const id = parseInt(setupMatch[0]);
    const idx = setupDb.findIndex(s => s.id === id);
    if (idx >= 0) setupDb[idx] = { ...setupDb[idx], ...body };
    return setupDb[idx];
  }

  if (base === '/api/config/config') {
    Object.assign(configData, body);
    return { ...configData };
  }

  if (base === '/api/auth/trocar-senha-primeiro-acesso') {
    return { sucesso: true };
  }

  if (base === '/api/planejamento') {
    return { itens: body?.itens || [], status: 'PLANEJADO' };
  }

  return { sucesso: true };
}

function handleDelete(path) {
  const base = path.split('?')[0];

  const manutencaoMatch = matchPath(base, '/api/manutencao/:id');
  if (manutencaoMatch) {
    const id = parseInt(manutencaoMatch[0]);
    const idx = manutencaoDb.findIndex(m => m.id === id);
    if (idx >= 0) manutencaoDb.splice(idx, 1);
    return { sucesso: true };
  }

  const agendamentoMatch = matchPath(base, '/api/agendamento/:id');
  if (agendamentoMatch) {
    const id = parseInt(agendamentoMatch[0]);
    const idx = agendamentoDb.findIndex(a => a.id === id);
    if (idx >= 0) agendamentoDb.splice(idx, 1);
    return { sucesso: true };
  }

  return { sucesso: true };
}

async function request(path, { method = 'GET', body } = {}) {
  await fakeDelay(100 + Math.random() * 200);

  try {
    switch (method) {
      case 'GET': return handleGet(path);
      case 'POST': return handlePost(path, body);
      case 'PUT': return handlePut(path, body);
      case 'DELETE': return handleDelete(path);
      default: return handleGet(path);
    }
  } catch (e) {
    throw new Error('Erro interno do mock');
  }
}

export const api = {
  get: (path) => request(path),
  post: (path, body) => request(path, { method: 'POST', body }),
  put: (path, body) => request(path, { method: 'PUT', body }),
  delete: (path) => request(path, { method: 'DELETE' }),
};

export const apiUrl = (path) => path || '';
